// Data hook for the admin-gated `/admin/access` users table. Mirrors
// `useAnalyticsOverview` (analytics/useAnalytics.ts): fetch-on-mount, a 403 sets
// `forbidden` so the page renders its denied state off the server gate (no role
// lives in the cookie). The mutation helpers (`setStatus`/`setLevel`/`revoke`)
// hit the PATCH/PUT/DELETE endpoints; callers `reload()` after a mutation so the
// table re-reads the authoritative server state rather than patching locally.

import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import {
  AdminUsersResponseSchema,
  type AccessLevelWire,
  type AccessStatusWire,
  type AdminUserRow,
} from "@loom/shared/admin";

import {
  HttpError,
  UnauthorizedError,
  httpDelete,
  httpGet,
  httpPatch,
  httpPut,
} from "../lib/http";

export interface AccessState {
  readonly users: ReadonlyArray<AdminUserRow>;
  readonly loading: boolean;
  readonly error: string | null;
  /** The server 403'd — caller is not an administrator. */
  readonly forbidden: boolean;
  /** Re-fetch the users list (call after a mutation). */
  readonly reload: () => void;
  /** PATCH a user's approval status. */
  readonly setStatus: (userId: string, status: AccessStatusWire) => Promise<void>;
  /** PUT a per-project grant level (create or update). */
  readonly setLevel: (
    userId: string,
    projectId: number,
    level: AccessLevelWire,
  ) => Promise<void>;
  /** DELETE a per-project grant. */
  readonly revoke: (userId: string, projectId: number) => Promise<void>;
}

export const useAccess = (): AccessState => {
  const [users, setUsers] = useState<ReadonlyArray<AdminUserRow>>([]);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const aborted = useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    httpGet("/api/admin/access/users", AdminUsersResponseSchema)
      .then((value) => {
        if (aborted.current) return;
        setUsers(value.users);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (aborted.current) return;
        if (err instanceof UnauthorizedError) {
          // Route loader redirects to /sign-in; keep loading, don't flash.
          return;
        }
        if (err instanceof HttpError && err.status === 403) {
          setForbidden(true);
          setLoading(false);
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    aborted.current = false;
    load();
    return () => {
      aborted.current = true;
    };
  }, [load]);

  const setStatus = useCallback(
    async (userId: string, status: AccessStatusWire): Promise<void> => {
      // Response body is unused (the caller reloads the authoritative list);
      // parse permissively so a `{ok:true}`-vs-row shape change can't throw.
      await httpPatch(
        `/api/admin/access/users/${encodeURIComponent(userId)}`,
        { status },
        z.unknown(),
      );
    },
    [],
  );

  const setLevel = useCallback(
    async (
      userId: string,
      projectId: number,
      level: AccessLevelWire,
    ): Promise<void> => {
      await httpPut(
        `/api/admin/access/users/${encodeURIComponent(userId)}/projects/${projectId}`,
        { level },
        z.unknown(),
      );
    },
    [],
  );

  const revoke = useCallback(
    async (userId: string, projectId: number): Promise<void> => {
      await httpDelete(
        `/api/admin/access/users/${encodeURIComponent(userId)}/projects/${projectId}`,
      );
    },
    [],
  );

  return { users, loading, error, forbidden, reload: load, setStatus, setLevel, revoke };
};
